import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Link } from 'react-router-dom';
import apiClient from '../../utils/api';
import { localToday } from '../../utils/date';
import { applyTemplateNow } from '../../utils/templateApply';
import { Button } from '../ui/Button';

// Quick questions + One tap (Log page, 2026-10 restyle). One question at a
// time, drawn from whatever the backend still genuinely doesn't know
// (services/interview.py): never anything a device or your traits already
// answered, which is why the list shrinks over time.
//
// Three answers are always possible and mean different things: a value,
// "none today" (real data, counts as a zero), and "skip" (recorded, never a
// measurement). Ratings get no "none": there is no zero mood.

const todayStr = () => localToday();

const Pill = ({ on, big, className = '', ...props }) => (
  <button
    type="button"
    className={`rounded-full border font-medium transition-colors disabled:opacity-50 ${big ? 'px-5 py-2.5 text-base' : 'px-4 py-2 text-base'} ${
      on ? 'border-fg-3 bg-elev text-fg' : 'border-line-strong text-fg-2 hover:border-fg-3 hover:bg-elev hover:text-fg'
    } ${className}`}
    {...props}
  />
);

const Tile = ({ on, children, ...props }) => (
  <button
    type="button"
    className={`grid min-h-[64px] place-items-center rounded-xl border px-1.5 py-3 text-[1.15rem] font-medium tabular-nums transition-colors disabled:opacity-50 ${
      on ? 'border-fg-3 bg-elev text-fg' : 'border-line-strong text-fg-2 hover:border-fg-3 hover:bg-elev hover:text-fg'
    }`}
    {...props}
  >
    {children}
  </button>
);

const Quiet = ({ children, ...props }) => (
  <button type="button" className="bg-transparent p-0 text-sm font-medium text-fg-3 hover:text-fg disabled:opacity-50" {...props}>{children}</button>
);

const DailyInterview = ({ refreshKey = 0, defaultDate, onLogged }) => {
  const day = defaultDate || todayStr();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState(null);
  const [dumpOpen, setDumpOpen] = useState(false);
  const [dumpText, setDumpText] = useState('');

  const question = data?.questions?.[0] || null;

  const load = useCallback(async () => {
    try {
      const { data: body } = await apiClient.get('/api/interview/today', { params: { day } });
      setData(body);
    } catch {
      setData(null);
    }
  }, [day]);

  useEffect(() => { load(); }, [load, refreshKey]);

  useEffect(() => {
    if (!question) { setDraft(null); return; }
    if (question.control === 'number' && question.unit !== '1-10' && question.min != null) {
      const step = question.step || 1;
      setDraft(Math.round((question.min + question.max) / 2 / step) * step);
    } else if (question.control === 'time') {
      setDraft('19:00');
    } else {
      setDraft(null);
    }
  }, [question]);

  const send = async (payload) => {
    setBusy(true);
    try {
      const { data: body } = await apiClient.post('/api/interview/answer', payload, { params: { day } });
      setData((prev) => ({ ...prev, ...body }));
      onLogged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not save that');
    } finally {
      setBusy(false);
    }
  };

  const answer = (value) => send({ field: question.field, value, action: 'answer' });
  const nothing = () => send({ field: question.field, action: 'zero' });
  const skip = () => send({ field: question.field, action: 'decline' });

  const sweep = async () => {
    setBusy(true);
    try {
      const { data: body } = await apiClient.post('/api/interview/nothing-else', null, { params: { day } });
      setData((prev) => ({ ...prev, ...body }));
      onLogged?.();
    } catch {
      toast.error('Could not save that');
    } finally {
      setBusy(false);
    }
  };

  const submitDump = async () => {
    if (!dumpText.trim()) return;
    setBusy(true);
    try {
      const { data: body } = await apiClient.post('/api/interview/brain-dump', { text: dumpText, day });
      const count = Object.keys(body.captured || {}).length;
      toast.success(count ? `Caught ${count} thing${count === 1 ? '' : 's'}` : 'Nothing I could pin down in that');
      setDumpText('');
      setDumpOpen(false);
      setData((prev) => ({ ...prev, ...body.plan }));
      onLogged?.();
    } catch {
      toast.error('Could not read that');
    } finally {
      setBusy(false);
    }
  };

  const suggestion = data?.routine_suggestion || null;

  const acceptSuggestion = async () => {
    setBusy(true);
    try {
      await apiClient.post(`/api/templates/suggestions/${suggestion.id}/accept`);
      toast.success('Saved as a one-tap routine.');
      await load();
    } catch {
      toast.error('Could not save that routine');
    } finally {
      setBusy(false);
    }
  };

  const dismissSuggestion = async () => {
    setBusy(true);
    try {
      await apiClient.post(`/api/templates/suggestions/${suggestion.id}/dismiss`);
      await load();
    } catch {
      toast.error('Could not dismiss that');
    } finally {
      setBusy(false);
    }
  };

  const finish = async () => {
    try {
      await apiClient.post('/api/interview/complete', { day });
      load();
    } catch { /* not worth interrupting anyone over */ }
  };

  const tapRoutine = async (routine) => {
    setBusy(true);
    try {
      const { count } = await applyTemplateNow({ id: routine.template_id, name: routine.label, items: routine.items || [] }, day);
      toast[count ? 'success' : 'error'](count ? `${routine.label} logged` : 'That routine has nothing to log yet.');
      await load();
      onLogged?.();
    } catch {
      toast.error('Could not log that');
    } finally {
      setBusy(false);
    }
  };

  if (!data) return null;

  const total = data.core_total || 0;
  const answered = data.answered || 0;
  const setupMissing = data.setup?.missing_essential?.length || 0;
  const isRating = question?.control === 'number' && question.unit === '1-10';
  const upNext = (data.questions || []).slice(1, 3);
  const dots = Math.min(total, 10);

  return (
    <>
      <section className="mt-10">
        <h2 className="mb-3.5 text-[1.2rem] font-medium tracking-[-0.015em]">Quick questions</h2>

        <div className="rounded-2xl border border-line px-[22px] pb-[18px] pt-[22px]">
          {/* Progress: a dot per question, green when answered, white for the one in hand. */}
          <div className="flex items-center gap-2.5 text-sm text-fg-3">
            <span className="inline-flex gap-[5px]" aria-hidden="true">
              {Array.from({ length: dots }).map((_, i) => (
                <b key={i} className={`block h-2 w-2 rounded-full ${i < answered ? 'bg-good-strong dark:bg-good' : i === answered && question ? 'bg-fg' : 'bg-line-strong'}`} />
              ))}
            </span>
            {answered} of {total}
            {data.streak > 0 && <span className="ml-auto">{data.streak} day{data.streak === 1 ? '' : 's'} in a row</span>}
          </div>

          {question ? (
            <>
              <h3 className="mt-2.5 text-[1.5rem] font-medium leading-[1.2] tracking-[-0.02em]">{question.prompt}</h3>

              {isRating && (
                <div className="mt-[18px] grid grid-cols-5 gap-2 sm:grid-cols-10">
                  {Array.from({ length: question.max - question.min + 1 }, (_, i) => question.min + i).map((n) => (
                    <Tile key={n} disabled={busy} onClick={() => answer(n)}>{n}</Tile>
                  ))}
                </div>
              )}

              {question.control === 'toggle' && (
                <div className="mt-[18px] flex flex-wrap gap-2">
                  <Pill big disabled={busy} onClick={() => answer(true)}>Yes</Pill>
                  <Pill big disabled={busy} onClick={() => answer(false)}>No</Pill>
                </div>
              )}

              {question.control === 'choice' && (
                <div className="mt-[18px] flex flex-wrap gap-2">
                  {question.choices.map((choice) => (
                    <Pill big key={choice} disabled={busy} onClick={() => answer(choice)} className="capitalize">{choice}</Pill>
                  ))}
                </div>
              )}

              {question.control === 'number' && !isRating && (
                <div className="mt-[18px]">
                  <div className="flex items-baseline gap-2">
                    <span className="text-[2rem] font-medium leading-none tracking-[-0.02em] tabular-nums">{draft ?? question.min}</span>
                    <span className="text-base text-fg-3">{question.unit}</span>
                  </div>
                  <input
                    type="range"
                    min={question.min ?? 0}
                    max={question.max ?? 10}
                    step={question.step ?? 1}
                    value={draft ?? question.min ?? 0}
                    onChange={(e) => setDraft(Number(e.target.value))}
                    className="mt-3 w-full accent-fg"
                  />
                </div>
              )}

              {question.control === 'time' && (
                <div className="mt-[18px]">
                  <input
                    type="time"
                    value={draft ?? '19:00'}
                    onChange={(e) => setDraft(e.target.value)}
                    className="h-12 rounded-full border border-line-strong bg-transparent px-4 text-base text-fg"
                  />
                </div>
              )}

              {question.control === 'text' && (
                <textarea
                  rows={2}
                  value={draft ?? ''}
                  onChange={(e) => setDraft(e.target.value)}
                  className="mt-[18px] w-full rounded-xl border border-line-strong bg-transparent p-3 text-base text-fg placeholder:text-fg-3 focus:border-fg-3 focus:outline-none"
                />
              )}

              <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2">
                {(question.control === 'number' && !isRating) || question.control === 'time' || question.control === 'text' ? (
                  <Button onClick={() => answer(draft)} disabled={busy || (question.control === 'text' && !draft)} className="px-[22px]">Save</Button>
                ) : null}
                {question.zeroable && <Quiet onClick={nothing} disabled={busy}>None today</Quiet>}
                <span className="flex-1" />
                <Quiet onClick={skip} disabled={busy}>Skip this one</Quiet>
              </div>
            </>
          ) : (
            <div className="mt-2.5">
              <h3 className="text-[1.5rem] font-medium leading-[1.2] tracking-[-0.02em]">That’s everything for today.</h3>
              {data.coverage && (
                <p className="mt-1.5 text-fg-3">
                  We know {data.coverage.known} of {data.coverage.total} things about today, most of it without asking.
                </p>
              )}
              {data.set_aside > 0 && (
                <p className="mt-2 text-sm text-fg-3">
                  {data.set_aside} more question{data.set_aside === 1 ? '' : 's'} set aside by your {data.thoroughness} setting.{' '}
                  <Link to="/me/insights" className="font-medium text-fg-2 hover:text-fg">Ask me everything</Link>
                </p>
              )}
              {!data.complete && <Button className="mt-4" variant="outline" onClick={finish}>Done</Button>}
            </div>
          )}

          {/* The fast path: say the whole day in one go. */}
          {question && (
            <div className="mt-4 border-t border-line pt-3.5">
              {dumpOpen ? (
                <div>
                  <textarea
                    rows={3}
                    autoFocus
                    value={dumpText}
                    onChange={(e) => setDumpText(e.target.value)}
                    placeholder="Slept badly, coffee at 7, walked at lunch, two beers…"
                    className="w-full rounded-xl border border-line-strong bg-transparent p-3 text-base text-fg placeholder:text-fg-3 focus:border-fg-3 focus:outline-none"
                  />
                  <div className="mt-2 flex items-center gap-3">
                    <Button size="sm" onClick={submitDump} disabled={busy || !dumpText.trim()}>Read it</Button>
                    <Quiet onClick={() => setDumpOpen(false)}>Cancel</Quiet>
                  </div>
                </div>
              ) : (
                <div className="flex flex-wrap gap-x-4 gap-y-2">
                  <Quiet onClick={() => setDumpOpen(true)}>Describe the whole day instead</Quiet>
                  {data.questions.some((q) => q.zeroable) && (
                    <Quiet onClick={sweep} disabled={busy}>Nothing else happened</Quiet>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {upNext.length > 0 && (
          <div className="mt-3 flex flex-wrap items-center gap-2 text-sm text-fg-3">
            Next:
            {upNext.map((q) => (
              <span key={q.field} className="rounded-full border border-line px-3 py-1.5">{q.prompt}</span>
            ))}
          </div>
        )}

        {/* Answering these once removes questions for good. */}
        {setupMissing > 0 && (
          <p className="mt-3 text-sm text-fg-3">
            {setupMissing} quick question{setupMissing === 1 ? '' : 's'} about you, answered once, and we stop asking every day.{' '}
            <Link to="/me/traits" className="font-medium text-fg-2 hover:text-fg">Set these up →</Link>
          </p>
        )}
        {data.location && !data.location.resolved && (
          <p className="mt-2 text-sm text-fg-3">
            {data.location.note}{' '}
            <Link to="/me/health" className="font-medium text-fg-2 hover:text-fg">
              {data.location.location ? 'Fix your location →' : 'Add your location →'}
            </Link>
          </p>
        )}

        {suggestion && (
          <div className="mt-3 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-line px-5 py-4">
            <p className="text-base">
              You’ve logged <span className="font-medium">{suggestion.icon ? `${suggestion.icon} ` : ''}{suggestion.name}</span> {suggestion.seen_days} times lately. Make it one tap?
            </p>
            <div className="flex items-center gap-3">
              <Button size="sm" onClick={acceptSuggestion} disabled={busy}>Make it a routine</Button>
              <Quiet onClick={dismissSuggestion} disabled={busy}>No thanks</Quiet>
            </div>
          </div>
        )}
      </section>

      {data.routines?.length > 0 && (
        <section className="mt-10">
          <div className="mb-3.5 flex flex-wrap items-baseline justify-between gap-4">
            <div>
              <h2 className="text-[1.2rem] font-medium tracking-[-0.015em]">One tap</h2>
              <p className="mt-1 text-sm text-fg-3">Things you do most days. Tap when done.</p>
            </div>
            <Link to="/me/checklist" className="text-sm font-medium text-fg-2 hover:text-fg">Change these →</Link>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {data.routines.map((r) => (
              <button
                key={r.id}
                type="button"
                disabled={busy || r.done}
                onClick={() => tapRoutine(r)}
                className="flex min-h-[72px] items-center gap-3 rounded-xl border border-line bg-transparent px-[18px] py-4 text-left transition-colors hover:bg-raised disabled:cursor-default disabled:hover:bg-transparent"
              >
                <i className={`grid h-6 w-6 flex-none place-items-center rounded-full border-[1.5px] ${r.done ? 'border-good-strong bg-good-strong dark:border-good dark:bg-good' : 'border-fg-3'}`} aria-hidden="true">
                  {r.done && <span className="mb-0.5 h-[11px] w-1.5 rotate-45 border-b-2 border-r-2 border-white" />}
                </i>
                <span className="min-w-0">
                  <b className={`block text-[1.05rem] font-medium ${r.done ? 'text-fg-2' : ''}`}>{r.icon ? `${r.icon} ` : ''}{r.label}</b>
                  <small className="mt-0.5 block text-sm text-fg-3">{r.done ? 'Done' : (r.items?.length ? `${r.items.length} item${r.items.length === 1 ? '' : 's'}` : 'Tap to log')}</small>
                </span>
              </button>
            ))}
          </div>
        </section>
      )}
    </>
  );
};

export default DailyInterview;
